import { createContext, useEffect } from 'react'
import { ReactNode, useState } from 'react'
import { Socket } from 'socket.io-client'
import { useStore } from 'react-redux'
import { initWebSocket, registerConnectionChangedListener } from '../@core/lib/wsClient'
import type { AppDispatch } from '../store'
import { addNotification, fetchUserNotifications } from '../store/apps/resto365Notifications'
import {
  notificationFromSocketPayload,
  onResto365SocketNotificationDelivered
} from '../@core/lib/resto365NotificationInbound'

type WebSocketContextType = {
  socket: Socket | null
  isConnected: boolean
}
const defaultProvider: WebSocketContextType = {
  socket: null,
  isConnected: false
}

const WebSocketContext = createContext<WebSocketContextType>(defaultProvider)

const WebSocketProvider = ({ children }: { children: ReactNode }) => {
  const store = useStore()
  const [isConnected, setIsConnected] = useState(false)
  const [socket, setSocket] = useState<Socket | null>(null)

  useEffect(() => {
    const init = async () => {
      registerConnectionChangedListener((isConnected: boolean) => {
        setIsConnected(isConnected)
      })

      const socket: Socket | null = await initWebSocket()
      if (socket) {
        setSocket(socket)

        // Check if socket is already connected
        if (socket.connected) {
          setIsConnected(true)
        }
      }
    }
    init()
  }, [])

  useEffect(() => {
    if (!socket) return

    const syncFromServer = () => {
      void (store.dispatch as AppDispatch)(fetchUserNotifications())
    }

    syncFromServer()
    socket.on('connect', syncFromServer)

    return () => {
      socket.off('connect', syncFromServer)
    }
  }, [socket, store])

  useEffect(() => {
    if (!socket) return

    const handler = (raw: unknown) => {
      const notification = notificationFromSocketPayload(raw)
      onResto365SocketNotificationDelivered(notification.message)
      ;(store.dispatch as AppDispatch)(addNotification(notification))
    }

    socket.on('notification', handler)

    return () => {
      socket.off('notification', handler)
    }
  }, [socket, store])

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        isConnected
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export { WebSocketContext, WebSocketProvider }
